import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const PreviewContainer = styled("div")(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "8px",
  border: "1px solid #DBDBDB",
  overflow: "hidden",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  maxWidth: "470px",
}));

const PostHeader = styled("div")(({ theme }) => ({
  padding: "14px 4px 14px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const UserSection = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
}));

const Avatar = styled("div")(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)",
  padding: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,

  "& .inner": {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  "& .initial": {
    background: "#E91E63",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: "14px",
    borderRadius: "50%",
  },
}));

const InstagramIcon = styled("div")(({ theme }) => ({
  width: "24px",
  height: "24px",
  borderRadius: "6px",
  background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,

  "& svg": {
    width: "14px",
    height: "14px",
    fill: "#FFFFFF",
  },
}));

const InstagramLogoSVG = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const UserName = styled("div")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "#262626",
}));

const CarouselContainer = styled("div")(({ theme }) => ({
  width: "100%",
  position: "relative",
  backgroundColor: "#000000",
  overflow: "hidden",
}));

const CarouselTrack = styled("div")(({ theme }) => ({
  display: "flex",
  transition: "transform 0.3s ease-in-out",
}));

const CarouselSlide = styled("div")(({ theme }) => ({
  minWidth: "100%",
  aspectRatio: "1 / 1",
  position: "relative",
  backgroundColor: "#FAFAFA",

  "& img, & video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

const MediaPlaceholder = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  background: "#F0F0F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "12px",
  color: "#737373",

  "& .icon": {
    width: "62px",
    height: "62px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& svg": {
      width: "100%",
      height: "100%",
      fill: "#A8A8A8",
    },
  },

  "& .text": {
    fontSize: "14px",
    color: "#737373",
  },
}));

const ImagePlaceholderIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
    <path
      d="M3 17L7 13L11 17L15 13L21 19"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VideoOverlay = styled("div")(({ theme, isPlaying }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  display: isPlaying ? "none" : "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "opacity 0.2s",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "28px",
    color: "#FFFFFF",
  },
}));

const CarouselButton = styled("button")(({ theme, direction }) => ({
  position: "absolute",
  top: "50%",
  [direction]: "8px",
  transform: "translateY(-50%)",
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.85)",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  zIndex: 2,

  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
    color: "#262626",
  },
}));

const CarouselDots = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "12px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: "4px",
  zIndex: 2,
}));

const Dot = styled("div")(({ theme, active }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: active ? "#0095F6" : "rgba(255, 255, 255, 0.5)",
  transition: "background-color 0.2s",
}));

const PostActions = styled("div")(({ theme }) => ({
  padding: "10px 16px 8px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const ActionGroup = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "16px",
}));

const ActionButton = styled("button")(({ theme }) => ({
  background: "transparent",
  border: "none",
  padding: "8px",
  // cursor: "pointer",
  color: "#262626",
  display: "flex",
  alignItems: "center",
  margin: "-8px",

  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },

  // "&:hover": {
  //   opacity: 0.5,
  // },
}));

const PostStats = styled("div")(({ theme }) => ({
  padding: "0 16px",
  fontSize: "14px",
  color: "#262626",
  fontWeight: "600",
  marginBottom: "8px",
}));

const PostCaption = styled("div")(({ theme }) => ({
  padding: "0 16px",
  fontSize: "14px",
  lineHeight: "18px",
  color: "#262626",
  marginBottom: "4px",

  "& .username": {
    fontWeight: "600",
    marginRight: "6px",
  },

  "& .caption_text": {
    display: "inline",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
  },

  "& .more": {
    color: "#8E8E8E",
    // cursor: "pointer",
    marginLeft: "4px",
  },
}));

const ViewComments = styled("div")(({ theme }) => ({
  padding: "0 16px",
  fontSize: "14px",
  color: "#8E8E8E",
  // cursor: "pointer",
  marginBottom: "8px",

  // "&:hover": {
  //   opacity: 0.6,
  // },
}));

const PostTime = styled("div")(({ theme }) => ({
  padding: "0 16px 16px 16px",
  fontSize: "10px",
  color: "#8E8E8E",
  textTransform: "uppercase",
  letterSpacing: "0.2px",
}));

const InstagramPreview = ({ modalData, showStats = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const userName = modalData?.userName || "UserName";
  const caption = modalData?.caption || "";
  const images = modalData?.images || [];
  const videoUrl = modalData?.videoUrl;
  const likes = modalData?.likes || 12542;
  const comments = modalData?.comments || 123;
  const timeAgo = modalData?.timeAgo || "2 HOURS AGO";
  const profileImage = modalData?.profileImage;
  const showInstagramIcon = modalData?.showInstagramIcon || false;

  // Handle multiple images or single image
  const mediaItems =
    images.length > 0
      ? images
      : modalData?.imageUrl
        ? [modalData.imageUrl]
        : modalData?.videoUrl
          ? [modalData.videoUrl]
          : [];

  const hasMultipleImages = mediaItems.length > 1;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % mediaItems.length);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
    );
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  const isVideoFile = (value) => {
    if (!value) return false;
    return /\.(mp4|mov|avi|webm|mkv)(\?|#|$)/i.test(value);
  };
  const isVideo =
    videoUrl ||
    (mediaItems[currentSlide] && isVideoFile(mediaItems[currentSlide]));

  return (
    <PreviewContainer>
      <PostHeader>
        <UserSection>
          {showInstagramIcon ? (
            <InstagramIcon>
              <InstagramLogoSVG />
            </InstagramIcon>
          ) : (
            <Avatar>
              <div className="inner">
                {profileImage ? (
                  <img src={profileImage} alt={userName} />
                ) : (
                  <div className="initial">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </Avatar>
          )}
          <UserName>{userName}</UserName>
        </UserSection>
        <MoreHorizIcon
          sx={{
            color: "#262626",
            cursor: "pointer",
            padding: "8px",
            margin: "-8px",
          }}
        />
      </PostHeader>

      <CarouselContainer>
        {mediaItems.length > 0 ? (
          <>
            <CarouselTrack
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {mediaItems.map((media, index) => (
                <CarouselSlide key={index}>
                  {isVideo && index === currentSlide ? (
                    <>
                      <video
                        ref={videoRef}
                        onClick={handleVideoClick}
                        onEnded={handleVideoEnded}
                        style={{ cursor: "pointer" }}
                      >
                        <source src={media} type="video/mp4" />
                      </video>
                      <VideoOverlay
                        onClick={handleVideoClick}
                        isPlaying={isPlaying}
                      >
                        <PlayArrowIcon />
                      </VideoOverlay>
                    </>
                  ) : (
                    <img src={media} alt={`Post content ${index + 1}`} />
                  )}
                </CarouselSlide>
              ))}
            </CarouselTrack>

            {hasMultipleImages && (
              <>
                {currentSlide > 0 && (
                  <CarouselButton direction="left" onClick={prevSlide}>
                    <ChevronLeftIcon />
                  </CarouselButton>
                )}
                {currentSlide < mediaItems.length - 1 && (
                  <CarouselButton direction="right" onClick={nextSlide}>
                    <ChevronRightIcon />
                  </CarouselButton>
                )}
                <CarouselDots>
                  {mediaItems.map((_, index) => (
                    <Dot key={index} active={index === currentSlide} />
                  ))}
                </CarouselDots>
              </>
            )}
          </>
        ) : (
          <CarouselSlide>
            <MediaPlaceholder>
              <div className="icon">
                <ImagePlaceholderIcon />
              </div>
              <div className="text">No Preview Available</div>
            </MediaPlaceholder>
          </CarouselSlide>
        )}
      </CarouselContainer>

      <PostActions>
        <ActionGroup>
          <ActionButton>
            <FavoriteBorderIcon />
          </ActionButton>
          <ActionButton>
            <ChatBubbleOutlineIcon sx={{ transform: "scaleX(-1)" }} />
          </ActionButton>
          <ActionButton>
            <SendOutlinedIcon />
          </ActionButton>
        </ActionGroup>
        <ActionButton>
          <BookmarkBorderIcon />
        </ActionButton>
      </PostActions>

      {showStats && likes > 0 && (
        <PostStats>{likes.toLocaleString()} Likes</PostStats>
      )}

      {caption && (
        <PostCaption>
          <span className="username">{userName}</span>
          <span className="caption_text">
            {caption.length > 100 ? (
              <>
                {caption.substring(0, 100)}...
                <span className="more">more</span>
              </>
            ) : (
              caption
            )}
          </span>
        </PostCaption>
      )}

      {comments > 0 && (
        <ViewComments>View all {showStats ? comments : null} comments</ViewComments>
      )}

      {showStats && <PostTime>{timeAgo}</PostTime>}
    </PreviewContainer>
  );
};

export default InstagramPreview;
